import { useLazyLoadQuery } from "react-relay";

import { accountQueueQuery } from "./queue-fragment";
import type { queueFragment_Query } from "@app/queries/queueFragment_Query.graphql";

// "Is this already on the queue?" — for buttons that live on other pages.
//
// The queue page disables "Rebuild metrics" when a rebuild is already queued.
// Every other button that starts queued work deserves the same, because the
// alternative is a second click producing a job whose only job is to wait behind
// the first and redo it. That is the "impossible to schedule obsolete actions"
// half of the queue, and it was previously honoured on exactly one page.
//
// COSTS NOTHING EXTRA. It runs the SAME query with the SAME variables the footer
// already runs on every authenticated page, so Relay serves it from the store;
// `store-or-network` is what makes that true. A separate smaller query would
// have been a second network round trip per page to learn something already in
// memory.
export function useQueueLock() {
  const data = useLazyLoadQuery<queueFragment_Query>(
    accountQueueQuery,
    { history: 5 },
    { fetchPolicy: "store-or-network" }
  );

  const queuedKinds = data.accountQueue.queuedKinds;

  return {
    queuedKinds,
    // A button asks about the kind it would start. Undefined kinds are never
    // locked — a caller naming a kind that does not exist should get a working
    // button and a server-side error, not a silently dead one.
    isQueued: (kind: string) => queuedKinds.includes(kind),
    // The one thing a locked button should say. Kept here so every locked button
    // in the app says it the same way.
    lockedTitle:
      "Already on the queue — queueing it again would do the same work twice",
  };
}
